<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 id="clinex-test-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="clinex-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col">
              <p>
                <a
                  href="https://www.vcgs.org.au/tests/clinical-exomes"
                  target="_blank"
                  >Exome sequencing</a
                >
                is used to investigate complex health and developmental
                problems. It’s often used by specialist groups, such as
                geneticists and neurologists, to investigate specific causes of
                well-known, but poorly understood conditions (like intellectual
                disability or brain malformations).
              </p>
              <v-row>
                <v-col class="custom-col">
                  <v-alert
                    class="pl-10 red--text"
                    style="background: #FFEBEE; border: 1px solid red"
                  >
                    <b
                      >Please note: Medicare-funded Exome testing has now moved
                      to a dedicated test option</b
                    >
                    <ol>
                      <li>Click 'Add more tests'</li>
                      <li>
                        Select the 'Childhood syndrome - diagnostic exome or
                        WGS' test option. This test will be added to your screen
                        above the 'Exome Sequencing' test.
                      </li>
                      <li>
                        Click 'Remove test' next to the 'Exome Sequencing' test
                        and click 'Yes' to confirm you want to remove the test
                      </li>
                    </ol>
                  </v-alert>
                </v-col>
              </v-row>
              <p>
                Patient to be referred to clinical geneticist or a relevant
                specialist for testing.
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Funding source*</span>
              <v-select
                item-disabled=""
                v-model="billTo"
                :items="billingOptions"
                item-text="key"
                item-value="value"
                :id="testId + '.billTo'"
                v-on:change="billingSelection"
                outlined
                :rules="ruleNotNull"
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Turn around time*</span>

              <v-select
                v-model="urgency"
                :items="urgencyTypes"
                :id="testId + '.urgency'"
                no-data-text="Select a funding source first"
                :disabled="urgencyDisabled"
                v-on:change="familyType = ''"
                outlined
                :rules="ruleNotNull"
                hide-details="auto"
              ></v-select>
            </v-col>

            <v-col class="custom-col">
              <span class="text-title">Family type*</span>
              <v-select
                v-model="familyType"
                :items="familyTypes"
                :id="testId + '.familyType'"
                no-data-text="Select a turn around time first"
                :rules="ruleNotNull"
                v-on:change="analysisType = ''"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
            <v-col class="custom-col">
              <span class="text-title">Analysis type*</span>
              <v-select
                v-model="analysisType"
                :items="analysisTypes"
                :id="testId + '.analysisType'"
                no-data-text="Select a family type first"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <!-- The acknowledge checkbox is only shown when it is a Comprehensive test -->
          <v-row v-if="isComprehensiveTest">
            <v-col cols="1">
              <v-checkbox
                v-model="ackCheckbox"
                :id="testId + '.criteriaCheckbox'"
                :rules="this.isComprehensiveTest ? [(v) => v] : []"
                hide-details
                ref="ackCheckboxRef"
              />
            </v-col>
            <v-col cols="11">
              <span
                style="display: inline-block;"
                class="ml-md-n4 ml-xl-n16"
                v-if="familyType === 'Singleton'"
              >
                I understand that if the sample type is EDTA blood, Whole Genome
                Sequencing will proceed instead of exome at the same price.
              </span>
              <span
                style="display: inline-block;"
                class="ml-md-n4 ml-xl-n16"
                v-if="familyType === 'Trio'"
              >
                I understand that if all samples are EDTA blood, Whole Genome
                Sequencing will proceed instead of exome at the same price.
              </span>
            </v-col>
          </v-row>
          <v-row v-if="showAckCheckboxError" class="red--text">
            <v-col>
              Sample criteria must be acknowledged
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <v-alert class="text-grey border-grey pl-10">
                <b>Turnaround time options:</b>
                <ul class="ml-auto">
                  <li>Rapid (+$1300): 15 business days</li>
                  <li>Standard trio (focused or comprehensive): 8-10 weeks</li>
                  <li>Standard focused singleton: 3-4 months</li>
                  <li>Standard comprehensive singleton: 4-6 months</li>
                </ul>
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <b class="text-title">Clinical features</b>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  hide-details="auto"
                  outlined
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                  ><template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert />
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <v-checkbox
                v-model="congenital"
                item-text="key"
                item-value="value"
                label="Symptom onset is congenital"
                :id="testId + '.congenital'"
                style="grid-area: congenital"
                hide-details="auto"
              >
              </v-checkbox>
            </v-col>
          </v-row>

          <v-row v-if="congenital === false">
            <v-col class="custom-col">
              <span class="text-title">Age of onset</span>
              <v-text-field
                v-model="ageOfOnset"
                :id="testId + '.ageOfOnset'"
                outlined
                hide-details="auto"
              ></v-text-field>
              <span class="text-hint">Age in years</span>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                rows="2"
                auto-grow
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                rows="1"
                auto-grow
                style="grid-area: familyHx"
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <v-checkbox
                v-model="suspectDiagnosis"
                :id="testId + '.suspectDiagnosis'"
                item-text="key"
                item-value="value"
                label="I strongly suspect a specific clinical diagnosis? E.g.Kabuki syndrome"
              >
              </v-checkbox>
            </v-col>
          </v-row>

          <v-row v-if="suspectDiagnosis">
            <v-col class="offset-1 custom-col">
              <span class="text-title"
                >Please provide your top gene candidates</span
              >
              <template v-if="brokenGenesApi">
                <BrokenApiAlert type="genes" />
              </template>
              <template v-else>
                <v-autocomplete
                  ref="autocomplete"
                  v-model="indGenes"
                  :id="testId + '.indGenes'"
                  :items="validGenesList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  persistent-hint
                  deletable-chips
                  @input="searchInputGenes = null"
                  :search-input.sync="searchInputGenes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handleGenesSearchChange"
                  :filter="filterGenesItems"
                  counter="5"
                  :rules="ruleMaxCustomGenes"
                >
                  <template v-slot:prepend-item v-if="isSearchingGenes">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ genesSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span
                  class="text-hint aligned-hint"
                  :style="{ 'margin-top': hintMarginTop }"
                  >Select up to 5 genes.</span
                >
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <b class="text-title">Gene panels</b>
              <template v-if="brokenGenePanelsApi">
                <BrokenApiAlert type="genePanels" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="genePanels"
                  :id="testId + '.genePanels'"
                  :items="genePanelsList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  item-color="blue"
                  deletable-chips
                  @input="searchInputPanels = null"
                  :search-input.sync="searchInputPanels"
                  outlined
                  hide-details="auto"
                  @update:search-input="handleGenePanelsSearchChange"
                  :filter="filterGenePanelsItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingGenePanels">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ genePanelsSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please choose the most relevant gene panels to be used in
                  analysis. You can search panels using a key word. e.g.
                  intellectual disability. The list will refine based on the
                  word entered.</span
                ></template
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <v-alert text color="info" outlined class="pb-0 mb-0">
                <p>
                  Detailed information on panel content is available at
                  <a href="https://panelapp-aus.org/" target="_blank"
                    ><b style="color: #00538b">PanelApp Australia</b></a
                  >
                </p>
              </v-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="familyType === trioFlag"
                title="Family member 1"
                :idPrefix="testId + '.fm0.'"
                ref="familyMember1"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="clinical_exome"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="familyType === trioFlag"
                style="grid-area: person2"
                title="Family member 2"
                :idPrefix="testId + '.fm1.'"
                ref="familyMember2"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="clinical_exome"
              />
            </v-col>
          </v-row>
        </v-container>

        <ClinexConsent
          ref="clinexConsent"
          :testName="testName"
          :testId="testId"
          :is-trio="familyType === trioFlag"
          :force-form-dirty="forceFormDirty"
        ></ClinexConsent>

        <TestPriceContainer
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />

        <v-btn
          class="primary-btn btn-padding-medium top-spaced-item"
          id="clinex_continue"
          @click="goToNextSection()"
        >
          Continue to next section
        </v-btn>
      </v-form>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FamilyMember from "../FamilyMember";
import GenePanelMixin from "../../mixins/GenePanelMixin";
import PhenotypesMixin from "../../mixins/PhenotypesMixin";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import GenesMixin from "../../mixins/GenesMixin";
import ClinexConsent from "./ClinexConsentContainer";
import { EventBus } from "@/event-bus";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceContainer from "../common/TestPriceContainer.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import BrokenApiAlert from "../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../common/BadSearchQueryAlert.vue";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
  },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
    familyType: function() {
      // When it is trio test
      if (this.familyType === this.trioFlag) {
        // Check that Trio was selected and get patient data via EventBus
        EventBus.$emit("getPatientData");

        // update selectedTrioTests in App component
        EventBus.$emit("selectTrioTest", this.testName);
      } else {
        // when it is singleton test, update selectedTrioTests in App component
        EventBus.$emit("deselectTrioTest", this.testName);
      }
    },
    billTo() {
      if (this.billTo === "PATIENT") {
        useSectionStore().deligateBillingAddress = "PATIENT";
      } else {
        useSectionStore().deligateBillingAddress = "OTHER";
      }
    },
    analysisType(newVal) {
      if (newVal === "Focused exome (1-400 genes)") {
        this.showAckCheckboxError = false;
      }
    },
    ackCheckbox() {
      const checkbox = this.$refs.ackCheckboxRef;
      if (!checkbox) return;

      this.showAckCheckboxError = checkbox.hasError;
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.clinexTestPricing = await this.execRetrieveJson(
      "json/pricing/clinex_pricing.json",
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");
    const element = document.getElementById("clinex-test-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },
  beforeDestroy() {
    EventBus.$emit("deselectTrioTest", this.testName);
  },
  mixins: [
    GenePanelMixin,
    FormRulesMixin,
    PhenotypesMixin,
    GenesMixin,
    RetrieveJSONMixin,
  ],

  name: "",

  components: {
    ClinexConsent,
    FamilyMember,
    TestPriceContainer,
    BrokenApiAlert,
    BadSearchQueryAlert,
  },

  data: () => ({
    valid: false,
    urgent: false,
    urgentReason: "",
    clinexTestPricing: null,
    suspectDiagnosis: "",
    congenital: false,
    urgencyTypes: [],
    genePanels: [],
    phenotypes: [],
    familyHx: "",
    clinicalNotes: "",
    ageOfOnset: "",
    urgency: "",
    urgencyDisabled: false,
    familyType: "",
    trioFlag: "Trio",
    analysisType: "",
    indGenes: [],
    billTo: "",
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    billingOptions: [
      { key: "Patient / Family", value: "PATIENT" },
      {
        key: "Clinical Service / Institution / Hospital",
        value: "PRACTITIONER",
      },
    ],
    searchInputPanels: null,
    searchInputGenes: null,
    searchInputPhenotypes: null,
    medicare: false,
    ackCheckbox: false,
    showAckCheckboxError: false,
  }),

  computed: {
    isComprehensiveTest() {
      return (
        this.analysisType === "Comprehensive exome (> 400 genes +/- Mendeliome)"
      );
    },
    hintMarginTop() {
      const validation = this.ruleMaxCustomGenes[0](this.indGenes);
      if (typeof validation === "string") {
        return "0px";
      }
      return "-24px";
    },
    familyTypes() {
      return this.urgency === ""
        ? []
        : Object.keys(this.clinexTestPricing[this.urgency]);
    },
    analysisTypes() {
      return this.urgency === "" || this.familyType === ""
        ? []
        : Object.keys(this.clinexTestPricing[this.urgency][this.familyType]);
    },
    getPriceDetails() {
      return this.urgency === "" ||
        this.familyType === "" ||
        this.analysisType === ""
        ? null
        : this.clinexTestPricing[this.urgency][this.familyType][
            this.analysisType
          ];
    },
    price() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.price;
      } else {
        return null;
      }
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.testCode;
      } else {
        return "";
      }
    },
  },

  methods: {
    billingSelection: function() {
      this.urgencyDisabled = false;
      this.urgencyTypes = Object.keys(this.clinexTestPricing);

      this.urgency = this.urgencyTypes[0];

      /** When familyType is Trio, the familyType value will not be automatically reset,
       * because we want to keep the entered info for Family members */
      if (this.familyType !== "Trio") {
        this.familyType = "";
      }

      /** AnalysisType will always be reset when Funding resource is changed */
      this.analysisType = "";
    },

    validateConsentForm: function() {
      if (typeof this.$refs.clinexConsent !== "undefined") {
        return this.$refs.clinexConsent.isFormValid();
      }

      return false;
    },
    validateFamilyMemberForms: function() {
      if (
        this.$refs.familyMember1.isFormValid() &&
        this.$refs.familyMember2.isFormValid()
      )
        return true;

      return false;
    },
    isFormValid: function() {
      const consentFormValidated = this.validateConsentForm();

      if (!consentFormValidated) return false;

      // After passing consent form validation, validate family member forms if it is trio test
      if (this.familyType === this.trioFlag) {
        const familyMemberFormValidated = this.validateFamilyMemberForms();

        if (!familyMemberFormValidated) return false;
      }

      return true;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];
      //Ordering family member in the array so it stays consistantly [fm0, fm1]
      if (this.familyType === "Trio") {
        const orderEntries = Object.entries(this.$refs)
          .filter(
            ([familyKey]) => familyKey.substring(0, 12) === "familyMember",
          )
          .sort();
        for (let [, familyComponent] of orderEntries) {
          familyDetails.push(familyComponent.transferData());
        }
      }

      // Works with watch.ackCheckbox. When it is a Comprehensive test, if acknowledge checkbox is not checked, show error message
      if (
        this.isComprehensiveTest &&
        !this.ackCheckbox &&
        !this.showAckCheckboxError
      ) {
        this.showAckCheckboxError = true;
      }

      let result = {
        urgent: this.urgency === "Rapid",
        urgency: this.urgency,
        urgentReason: this.urgency === "Rapid" ? "Rapid exome testing" : "",
        testType: this.testId,
        familyType: this.familyType,
        analysisType: this.analysisType,
        rapid: this.rapidTest,
        genePanels: this.getFinalGenePanels(this.genePanels),
        indGenes: this.getFinalIndGenes(this.indGenes),
        phenotypes: this.getFinalPhenotypes(this.phenotypes),
        ageOfOnset: this.congenital === true ? "Congenital" : this.ageOfOnset,
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        billTo: this.billTo,
      };

      const consentData = this.$refs.clinexConsent.transferData();

      // assign the data fields from consent direct into the top level of our data
      Object.assign(result, consentData);

      return result;
    },
  },
};
</script>
<style scoped>
.red-alert-border {
  border: 1px solid #ff0000 !important;
}
</style>
